/** Shared helpers for approval workflow step badges on Request Status Desk. */

const ROLE_SHORT = {
  MANAGER: "RM",
  REPORTING_MANAGER: "RM",
  REPORTINGMANAGER: "RM",
  HR: "HR",
  HUMAN_RESOURCES: "HR",
  CEO: "CEO",
  MANAGEMENT: "MGT",
  ADMIN: "ADM",
  FINANCE: "FIN",
  IT: "IT",
};

export function shortRoleLabel(role) {
  const raw = String(role || "").trim();
  if (!raw) return "?";
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (ROLE_SHORT[key]) return ROLE_SHORT[key];
  if (key.includes("MANAGER") || key === "RM") return "RM";
  if (key.includes("HR") || key.includes("HUMAN")) return "HR";
  if (key.includes("CEO")) return "CEO";
  const words = raw.replace(/[_-]/g, " ").trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

export function roleDisplayName(role) {
  const raw = String(role || "").trim();
  if (!raw) return "";
  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (key === "MANAGER" || key === "REPORTING_MANAGER" || key === "REPORTINGMANAGER" || key === "RM") {
    return "Reporting Manager";
  }
  if (ROLE_SHORT[key] === "HR" || key.includes("HR") || key.includes("HUMAN")) return "HR";
  if (key.includes("CEO")) return "CEO";
  if (key === "MANAGEMENT" || key === "MGT") return "Management";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isManagerRole(role) {
  const key = String(role || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return (
    key === "MANAGER" ||
    key === "REPORTING_MANAGER" ||
    key === "REPORTINGMANAGER" ||
    key === "RM" ||
    key.includes("REPORTING_MANAGER")
  );
}

/** Roles that map to a single person (always show that person's name). */
export function isIndividualAssigneeRole(role) {
  const key = String(role || "")
    .toUpperCase()
    .replace(/[\s/-]+/g, "_");
  if (isManagerRole(role)) return true;
  return (
    key === "EMPLOYEE" ||
    key === "EMPLOYEE_SELF" ||
    key === "SELF" ||
    key === "EMPLOYEE_/_SELF" ||
    key.includes("EMPLOYEE")
  );
}

export function normalizeWorkflowStepStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "approved" || s === "done" || s === "completed") return "Approved";
  if (s === "rejected" || s === "sent_back") return "Rejected";
  if (s === "current" || s === "in_progress") return "Current";
  if (s === "notification" || s === "notify") return "Notification";
  return "Pending";
}

/** Prefer a person name; strip role-only placeholders like "MANAGER". */
function personNameOrNull(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const upper = raw.toUpperCase().replace(/[\s-]+/g, "_");
  if (
    upper === "MANAGER" ||
    upper === "REPORTING_MANAGER" ||
    upper === "HR" ||
    upper === "CEO" ||
    upper === "MANAGEMENT" ||
    upper === "ADMIN" ||
    upper === "EMPLOYEE" ||
    upper === "EMPLOYEE_SELF" ||
    upper === "SELF" ||
    ROLE_SHORT[upper]
  ) {
    return null;
  }
  if (/^employee\s*\/\s*self$/i.test(raw)) return null;
  if (/^reporting\s+manager$/i.test(raw)) return null;
  // Long FYI labels are not person names
  if (/notification|already onboarded|fyi|no approval|awaiting workflow/i.test(raw)) return null;
  return raw;
}

/**
 * Pending line for detail headers (not list cards):
 * - Individual assignee (RM / Employee) → person name
 * - Shared role with resolved single person → person name
 * - Shared role with multiple people → role label until acted
 */
export function workflowPendingDisplay(request = {}) {
  const roleRaw = request.pendingLabel || request.pendingRole || "";
  const role = roleDisplayName(roleRaw) || String(roleRaw || "").trim();
  const person =
    personNameOrNull(request.pendingAssigneeDisplay) ||
    personNameOrNull(
      request.pendingWith &&
        request.pendingWith !== role &&
        request.pendingWith !== roleRaw
        ? request.pendingWith
        : null
    ) ||
    personNameOrNull(request.assigneeDisplay) ||
    personNameOrNull(request.pendingAt);

  if (isIndividualAssigneeRole(request.pendingRole || roleRaw)) {
    return person || role || "Awaiting workflow assignment";
  }
  if (person) return person;
  if (role) return role;
  if (request?.pendingWith && request.pendingWith !== "Pending") {
    return roleDisplayName(request.pendingWith) || request.pendingWith;
  }
  return "Awaiting workflow assignment";
}

/**
 * Popover / details / activity line for a workflow step:
 * - Notify-only → "Notification"
 * - Approved/Rejected → acting person's name (fallback role)
 * - Current/Pending individual role → person name
 * - Current/Pending shared role → role name until acted
 */
export function workflowStepDisplayLine(step, index = 0, request = {}) {
  if (!step) return "";
  if (step.isNotification || step.status === "Notification") return "Notification";
  if (step.displayText) return step.displayText;

  const status = normalizeWorkflowStepStatus(step.status);
  const acted =
    status === "Approved" || status === "Rejected";
  const actor =
    personNameOrNull(step.actor) ||
    personNameOrNull(step.approverName) ||
    personNameOrNull(step.assigneeDisplay);
  const individual = isIndividualAssigneeRole(step.role);
  const pendingPerson =
    personNameOrNull(request.pendingAssigneeDisplay) ||
    personNameOrNull(request.assigneeDisplay) ||
    personNameOrNull(request.pendingWith);

  if (acted) {
    return actor || roleDisplayName(step.role) || shortRoleLabel(step.role);
  }
  if (individual) {
    return (
      actor ||
      pendingPerson ||
      roleDisplayName(step.role) ||
      "Reporting Manager"
    );
  }
  return roleDisplayName(step.role) || shortRoleLabel(step.role);
}

function isNotifyOnlyRequest(request = {}) {
  return (
    !!request.notifyOnlyFyi ||
    request.deskMode === "notify" ||
    /notification|already onboarded|fyi only/i.test(
      String(request.pendingWith || request.assigneeDisplay || "")
    )
  );
}

/**
 * Build a full workflow steps list for desk UI from API rows / local requests.
 * Prefers explicit workflowSteps; otherwise merges policy snapshot + approvals + trail.
 */
export function resolveWorkflowSteps(request = {}) {
  if (isNotifyOnlyRequest(request)) {
    return [
      {
        stepOrder: 1,
        role: "Notification",
        label: "N",
        status: "Notification",
        isNotification: true,
        displayText: "Notification",
        actor: null,
        actedAt: null,
        reason: "",
      },
    ];
  }

  const assigneeName =
    personNameOrNull(request.pendingAssigneeDisplay) ||
    personNameOrNull(request.assigneeDisplay) ||
    personNameOrNull(request.pendingWith);
  const requesterKeys = new Set(
    [request.userName, request.raisedByName, request.employeeName]
      .map((v) =>
        String(v || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ")
      )
      .filter(Boolean)
  );
  const isRequesterPerson = (name) => {
    const key = String(name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    return Boolean(key && requesterKeys.has(key));
  };

  const enrich = (step, idx) => {
    const individual = isIndividualAssigneeRole(step.role);
    const status = normalizeWorkflowStepStatus(step.status);
    const acted = status === "Approved" || status === "Rejected";
    const rawActor =
      personNameOrNull(step.actor) || personNameOrNull(step.approverName);
    // Pending/Current must not show the requesting employee as the approver actor
    // (older RH / desk trails incorrectly stored the submitter as actor).
    const actorName =
      (acted || !isRequesterPerson(rawActor) ? rawActor : null) ||
      (individual && (status === "Current" || status === "Pending")
        ? assigneeName
        : null);
    const displayText = workflowStepDisplayLine(
      {
        ...step,
        actor: actorName,
        status,
        assigneeDisplay: individual ? actorName : null,
        // Recompute unless this is an explicit notification label
        displayText: step.isNotification ? step.displayText : undefined,
      },
      idx,
      request
    );
    return {
      ...step,
      label: step.label || shortRoleLabel(step.role),
      actor: acted || individual ? actorName || (acted ? null : displayText) : displayText,
      assigneeDisplay: individual ? actorName : null,
      displayText,
      isNotification: false,
    };
  };

  if (Array.isArray(request.workflowSteps) && request.workflowSteps.length > 0) {
    return request.workflowSteps.map((step, idx) =>
      enrich(
        {
          stepOrder: step.stepOrder ?? step.order ?? idx + 1,
          role: step.role || step.actor || step.label || `Step ${idx + 1}`,
          label: step.label || shortRoleLabel(step.role || step.actor),
          status: normalizeWorkflowStepStatus(step.status),
          actor: step.actor || step.actorName || step.approverName || null,
          actedAt: step.actedAt || null,
          reason: step.reason || step.comments || "",
          displayText: step.displayText,
          isNotification: !!step.isNotification,
        },
        idx
      )
    );
  }

  const snap = request.workflowSnapshot || {};
  const defined =
    (Array.isArray(snap.approvalSteps) && snap.approvalSteps.length
      ? snap.approvalSteps
      : null) ||
    (Array.isArray(snap.steps) && snap.steps.length ? snap.steps : null) ||
    [];

  const approvals = Array.isArray(request.approvals) ? request.approvals : [];
  const byOrder = new Map(
    approvals.map((a) => [Number(a.stepOrder ?? a.order), a])
  );

  if (defined.length > 0) {
    const current = Number(request.currentStep) || 0;
    return defined.map((step, idx) => {
      const order = Number(step.order ?? step.stepOrder ?? idx + 1);
      const row = byOrder.get(order);
      let status = "Pending";
      if (row?.action === "approved") status = "Approved";
      else if (row?.action === "rejected") status = "Rejected";
      else if (
        current === order &&
        (request.status === "Pending" ||
          request.status === "pending_approval" ||
          request.rawStatus === "pending_approval")
      ) {
        status = "Current";
      }
      return enrich(
        {
          stepOrder: order,
          role: step.role || `Step ${order}`,
          label: shortRoleLabel(step.role),
          status,
          actor: row?.approverName || row?.actor || null,
          actedAt: row?.actedAt || null,
          reason: row?.comments || row?.reason || "",
        },
        idx
      );
    });
  }

  const trail = Array.isArray(request.workflowTrail) ? request.workflowTrail : [];
  if (trail.length > 0) {
    return trail.map((step, idx) => {
      const rawRole = step.role || null;
      const actorLooksLikeRole =
        !!step.actor &&
        (isManagerRole(step.actor) ||
          !!ROLE_SHORT[
            String(step.actor || "")
              .toUpperCase()
              .replace(/[\s-]+/g, "_")
          ] ||
          /^reporting\s+manager$/i.test(String(step.actor || "")));
      const role =
        rawRole ||
        (actorLooksLikeRole ? step.actor : null) ||
        (isManagerRole(request.pendingRole) || !request.pendingRole
          ? request.pendingRole || "reporting_manager"
          : request.pendingRole) ||
        `Step ${idx + 1}`;
      const actor =
        actorLooksLikeRole || isRequesterPerson(step.actor) ? null : step.actor || null;
      return enrich(
        {
          stepOrder: idx + 1,
          role,
          label: step.label || shortRoleLabel(role),
          status: normalizeWorkflowStepStatus(step.status),
          actor,
          actedAt: step.actedAt || null,
          reason: step.reason || "",
          // Drop stale displayText that names the requester; recompute from RM assignee
          displayText:
            isRequesterPerson(step.displayText) || isRequesterPerson(step.actor)
              ? undefined
              : step.displayText,
          isNotification: !!step.isNotification,
        },
        idx
      );
    });
  }

  if (assigneeName) {
    const requestStatus = String(request.status || "").toLowerCase();
    const acted =
      requestStatus === "approved" || requestStatus === "rejected";
    const decidedActor = personNameOrNull(request.lastDecision?.actorName);
    return [
      enrich(
        {
          stepOrder: 1,
          role: acted ? "approver" : "MANAGER",
          label: acted ? "APR" : "RM",
          status:
            requestStatus === "approved"
              ? "Approved"
              : requestStatus === "rejected"
                ? "Rejected"
                : "Current",
          // Never attribute a decision to the pending RM assignee when someone else acted
          actor: acted ? decidedActor : assigneeName,
          actedAt: acted ? request.lastDecision?.actedAt || null : null,
          reason: acted ? request.lastDecision?.reason || "" : "",
        },
        0
      ),
    ];
  }

  return [];
}

export function buildLifecycleWorkflowTrail(row = {}) {
  const snap = row.workflowSnapshot || {};
  const steps =
    (Array.isArray(snap.approvalSteps) && snap.approvalSteps) ||
    (Array.isArray(snap.steps) && snap.steps) ||
    [];
  const approvals = Array.isArray(row.approvals) ? row.approvals : [];
  const byOrder = new Map(approvals.map((a) => [Number(a.stepOrder), a]));
  const current = Number(row.currentStep) || 0;
  const pendingName =
    personNameOrNull(row.pendingAssigneeDisplay) ||
    personNameOrNull(row.pendingLabel);
  const pendingStatuses = new Set([
    "pending_approval",
    "on_hold",
    "cancellation_pending",
    "extended",
  ]);
  const isPending = pendingStatuses.has(String(row.status || "").toLowerCase());

  if (steps.length > 0) {
    return steps.map((step, idx) => {
      const order = Number(step.order ?? idx + 1);
      const a = byOrder.get(order);
      let status = "Pending";
      if (a?.action === "approved") status = "Approved";
      else if (a?.action === "rejected" || a?.action === "cancelled") status = "Rejected";
      else if (a?.action === "hold") status = "Current";
      else if (current === order && isPending) status = "Current";
      const individual = isIndividualAssigneeRole(step.role);
      const acted = status === "Approved" || status === "Rejected";
      const actor =
        personNameOrNull(a?.approverName) ||
        (individual && (status === "Current" || status === "Pending")
          ? pendingName
          : null);
      const displayText = acted
        ? actor || roleDisplayName(step.role)
        : individual
          ? actor || roleDisplayName(step.role) || "Reporting Manager"
          : roleDisplayName(step.role);
      return {
        status,
        role: step.role,
        actor: actor || displayText,
        actedAt: a?.actedAt || null,
        reason: a?.comments || "",
        stepOrder: order,
        label: shortRoleLabel(step.role),
        displayText,
        isNotification: false,
      };
    });
  }

  return approvals.map((step) => {
    const individual = isIndividualAssigneeRole(step.role);
    const acted =
      step.action === "approved" || step.action === "rejected";
    const actor =
      personNameOrNull(step.approverName) ||
      (individual && !acted ? pendingName : null);
    const displayText = acted
      ? actor || roleDisplayName(step.role)
      : individual
        ? actor || roleDisplayName(step.role) || "Reporting Manager"
        : roleDisplayName(step.role);
    return {
      status:
        step.action === "approved"
          ? "Approved"
          : step.action === "rejected"
            ? "Rejected"
            : "Pending",
      role: step.role,
      actor: actor || displayText,
      actedAt: step.actedAt || null,
      reason: step.comments || "",
      stepOrder: step.stepOrder,
      label: shortRoleLabel(step.role),
      displayText,
      isNotification: false,
    };
  });
}

export function buildOnboardingWorkflowTrail(row = {}) {
  return buildLifecycleWorkflowTrail(row);
}
